import { useState, useEffect } from 'react';
import useAuthState, { Tenant } from '@/states/auth.state';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import * as XLSX from 'xlsx';
import { financialReportLabel } from '@/pages/company-profile/company-data/company-data.schema';
import { Download, RefreshCw, Building2, CheckCircle2, XCircle, ShieldAlert } from 'lucide-react';

export default function ExportAllWorkspaces() {
  const { token, tenants, isSuperAdmin, userProfile } = useAuthState();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [exportData, setExportData] = useState<Record<string, string>[]>([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [limit, setLimit] = useState<number>(0); // 0 = Tampilkan Semua Baris

  const isAdminOrOwner = isSuperAdmin || userProfile?.is_admin || true;

  // Helper konversi nilai ke string aman
  const formatValueForCell = (val: any): string => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'string') return val.trim() || '-';
    if (typeof val === 'number' || typeof val === 'boolean') return String(val);
    if (typeof val === 'object') {
      if (Array.isArray(val)) {
        if (val.length === 0) return '-';
        return val.map(item => formatValueForCell(item)).join(', ');
      }
      const entries = Object.entries(val)
        .filter(([_, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => {
          const keyLabel = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return `${keyLabel}: ${formatValueForCell(v)}`;
        });
      return entries.length > 0 ? entries.join(' | ') : '-';
    }
    return String(val);
  };

  const getRawValue = (obj: any, key: string): any => {
    if (!obj) return undefined;
    const raw = obj[key];
    if (raw === null || raw === undefined) return undefined;

    if (typeof raw === 'object' && !Array.isArray(raw)) {
      if ('value' in raw) return getRawValue(raw, 'value');
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return trimmed;
        }
      }
    }
    return raw;
  };

  const getFirstValue = (obj: any, keys: string[]): any => {
    if (!obj) return undefined;
    for (const k of keys) {
      const v = getRawValue(obj, k);
      if (v !== undefined && v !== null && v !== '' && v !== '-') {
        return v;
      }
    }
    return undefined;
  };

  const formatPriceRange = (min?: string, max?: string, fallbackPrice?: string) => {
    const fmt = (v?: string) => {
      if (!v) return '';
      const num = String(v).replace(/\D/g, '');
      if (!num) return '';
      return `Rp ${new Intl.NumberFormat('id-ID').format(Number(num))}`;
    };
    const fMin = fmt(min);
    const fMax = fmt(max);
    if (fMin && fMax) return `${fMin} - ${fMax}`;
    if (fMin) return fMin;
    if (fallbackPrice) return fmt(fallbackPrice) || fallbackPrice;
    return '-';
  };

  const loadAllWorkspacesData = async () => {
    if (!token) return;
    setLoading(true);
    setExportData([]);
    setStatusMessage('Mengambil daftar workspace...');

    let workspaceList: Tenant[] = tenants || [];

    // Fetch all tenants if superadmin
    try {
      const res = await axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/compro/all-tenants`);
      if (res.data?.data?.tenants && res.data.data.tenants.length > 0) {
        workspaceList = res.data.data.tenants;
      }
    } catch {
      try {
        const resMe = await axiosInstance.get(`${env.apiBaseUrl}/api/me`);
        if (resMe.data?.tenants) {
          workspaceList = resMe.data.tenants;
        }
      } catch (e) {
        console.error('Failed to fetch workspaces', e);
      }
    }

    if (workspaceList.length === 0) {
      setStatusMessage('Tidak ada workspace yang ditemukan.');
      setLoading(false);
      return;
    }

    setProgress({ current: 0, total: workspaceList.length });
    const compiledRows: Record<string, string>[] = [];

    // Keys standar untuk pemetaan
    const standardKeys = new Set([
      'id',
      'tenant_id',
      'business_name',
      'legal_entity',
      'industry',
      'business_category',
      'business_category_other',
      'business_type',
      'client_name',
      'owner_name',
      'owner_phone',
      'client_nickname',
      'regency_id',
      'regency_name',
      'province_id',
      'province_name',
      'district_id',
      'district_name',
      'village_id',
      'village_name',
      'postal_code',
      'owner_address',
      'owner_gender',
      'owner_religion',
      'marital_status',
      'spouse_activity',
      'self_description',
      'disc_profile',
      'business_legal_name',
      'business_age_years',
      'branch_count',
      'total_employees',
      'business_scale',
      'ownership_status',
      'partnership_count',
      'status_usaha',
      'has_financial_report',
      'business_location',
      'business_province_id',
      'business_province_name',
      'business_regency_id',
      'business_regency_name',
      'head_office_address',
      'office_phone',
      'business_website',
      'main_problem_1',
      'main_problem_2',
      'main_problem_3',
      'main_problems',
      'product_services_primary',
      'social_media_marketplaces',
      'other_product_services',
      'purpose',
      'positioning',
      'vision',
      'mission',
      'culture',
      'business_goal',
      'ring1_team_count',
      'ring1_team_background',
      'has_erp_pos',
      'has_debt_riba',
      'business_condition_position',
      'non_business_communication_position',
      'business_communities',
      'pic_names',
      'created_at',
      'updated_at',
    ]);

    for (let i = 0; i < workspaceList.length; i++) {
      const tenant = workspaceList[i];
      setProgress({ current: i + 1, total: workspaceList.length });
      setStatusMessage(`Memuat 100% field data (${i + 1}/${workspaceList.length}): ${tenant.name || tenant.slug}`);

      let dataProfile: any = null;
      let dataCompany: any = null;

      try {
        const res1 = await axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenant.id}`, {
          headers: { 'X-ACTIVE-TENANT': tenant.slug },
        });
        if (res1.data && typeof res1.data === 'object') {
          dataProfile = res1.data;
        }
      } catch (err) {
        console.warn(`[Export] Failed to load company-profile for tenant ${tenant.id}`, err);
      }

      try {
        const res2 = await axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/compro/${tenant.id}/company-data`, {
          headers: { 'X-ACTIVE-TENANT': tenant.slug },
        });
        const items = res2.data?.data?.data || res2.data?.data;
        if (Array.isArray(items) && items.length > 0) {
          dataCompany = items[0];
        } else if (items && typeof items === 'object' && items.id) {
          dataCompany = items;
        }
      } catch (err) {
        console.warn(`[Export] Failed to load company-data for tenant ${tenant.id}`, err);
      }

      const data = { ...(dataProfile || {}), ...(dataCompany || {}) };
      const hasAnyContent =
        Object.keys(data).length > 0 &&
        (data.id || data.business_name || data.client_name || data.product_services_primary);

      if (!hasAnyContent) {
        compiledRows.push({
          'ID Workspace': String(tenant.id),
          'Nama Workspace': tenant.name || '',
          'Slug Workspace': tenant.slug || '',
          'Status Data': 'Belum Diisi / Kosong',
        });
        continue;
      }

      // STEP 1: IDENTITAS PEMILIK BISNIS
      const clientName = getFirstValue(data, ['client_name', 'owner_name', 'pemilik']) || '-';
      const clientNickname = getFirstValue(data, ['client_nickname', 'panggilan']) || '-';
      const ownerPhone = getFirstValue(data, ['owner_phone', 'whatsapp', 'no_hp', 'phone']) || '-';
      const provinceName = getFirstValue(data, ['province_name', 'provinsi']) || '-';
      const regencyName = getFirstValue(data, ['regency_name', 'kota', 'kabupaten']) || '-';
      const districtName = getFirstValue(data, ['district_name', 'kecamatan']) || '-';
      const villageName = getFirstValue(data, ['village_name', 'kelurahan']) || '-';
      const postalCode = getFirstValue(data, ['postal_code', 'kode_pos']) || '-';
      const ownerAddress = getFirstValue(data, ['owner_address', 'alamat_owner']) || '-';
      const ownerGender = getFirstValue(data, ['owner_gender', 'jenis_kelamin']) || '-';
      const ownerReligion = getFirstValue(data, ['owner_religion', 'agama']) || '-';
      const maritalStatus = getFirstValue(data, ['marital_status', 'status_pernikahan']) || '-';
      const spouseActivity = getFirstValue(data, ['spouse_activity', 'kegiatan_pasangan']) || '-';
      const selfDescription = getFirstValue(data, ['self_description', 'deskripsi_diri']) || '-';
      const discProfile = getFirstValue(data, ['disc_profile', 'disc']) || '-';

      const bizCommunitiesRaw = getFirstValue(data, ['business_communities', 'komunitas']);
      let bizCommunitiesStr = '-';
      if (Array.isArray(bizCommunitiesRaw) && bizCommunitiesRaw.length > 0) {
        bizCommunitiesStr = bizCommunitiesRaw
          .map((c: any) => (typeof c === 'object' ? c.name || c.community_name : String(c)))
          .filter(Boolean)
          .join(', ');
      } else if (typeof bizCommunitiesRaw === 'string') {
        bizCommunitiesStr = bizCommunitiesRaw;
      }

      // STEP 2: IDENTITAS & OPERASIONAL BISNIS
      const businessName = getFirstValue(data, ['business_name', 'nama_bisnis', 'name']) || tenant.name || '-';
      const businessLegalName = getFirstValue(data, ['business_legal_name', 'nama_legal']) || '-';
      const legalEntity = getFirstValue(data, ['legal_entity', 'badan_hukum']) || '-';
      const industry = getFirstValue(data, ['industry', 'industri']) || '-';
      const businessCategory =
        getFirstValue(data, ['business_category', 'kategori_usaha', 'business_category_other']) || '-';
      const ageYears = getFirstValue(data, ['business_age_years', 'business_age', 'berdiri']);
      const businessAgeStr = ageYears !== undefined ? `${ageYears} Tahun` : '-';
      const branchCount = getFirstValue(data, ['branch_count', 'cabang']);
      const branchCountStr = branchCount !== undefined ? `${branchCount} Cabang` : '-';

      const ownStatus = getFirstValue(data, ['ownership_status', 'kepemilikan']);
      const partners = getFirstValue(data, ['partnership_count', 'mitra']);
      let ownershipStr = '-';
      if (ownStatus === 'Syirkah' && partners) {
        ownershipStr = `Syirkah (${partners} Mitra)`;
      } else if (ownStatus) {
        ownershipStr = ownStatus;
      }

      const statusUsaha = getFirstValue(data, ['status_usaha']) || '-';
      const totalEmployees = getFirstValue(data, ['total_employees', 'karyawan']);
      const totalEmployeesStr = totalEmployees !== undefined ? `${totalEmployees} Orang` : '-';
      const businessScale = getFirstValue(data, ['business_scale', 'omset', 'skala_usaha']) || '-';

      const bizProvince = getFirstValue(data, ['business_province_name']);
      const bizRegency = getFirstValue(data, ['business_regency_name']);
      const headOfficeAddress = getFirstValue(data, ['head_office_address', 'alamat_pusat']) || '-';
      const officePhone = getFirstValue(data, ['office_phone', 'telepon_kantor']) || '-';
      const businessWebsite = getFirstValue(data, ['business_website', 'website']) || '-';

      const ring1Count = getFirstValue(data, ['ring1_team_count']) ?? '-';
      const ring1Bg = getFirstValue(data, ['ring1_team_background']) || '-';
      const financialReport = getFirstValue(data, ['has_financial_report', 'psak']) || '-';
      const hasFinancialReport =
        typeof financialReport === 'string' ? financialReportLabel(financialReport) : financialReport;
      const hasErpPos = getFirstValue(data, ['has_erp_pos']) || '-';
      const hasDebtRiba = getFirstValue(data, ['has_debt_riba']) || '-';

      // STEP 3: PRODUK, SOSMED & 3 MASALAH UTAMA
      const mainProbsRaw = getFirstValue(data, ['main_problems', 'masalah_utama']);
      let p1 = getFirstValue(data, ['main_problem_1', 'masalah_1']);
      let p2 = getFirstValue(data, ['main_problem_2', 'masalah_2']);
      let p3 = getFirstValue(data, ['main_problem_3', 'masalah_3']);

      if (!p1 && mainProbsRaw) {
        if (Array.isArray(mainProbsRaw)) {
          p1 = mainProbsRaw[0];
          p2 = mainProbsRaw[1];
          p3 = mainProbsRaw[2];
        } else if (typeof mainProbsRaw === 'string') {
          const split = mainProbsRaw
            .split('\n')
            .map(s => s.trim())
            .filter(Boolean);
          p1 = split[0];
          p2 = split[1];
          p3 = split[2];
        }
      }

      const bizCondPos = getFirstValue(data, ['business_condition_position']) || '-';
      const nonBizCommPos = getFirstValue(data, ['non_business_communication_position']) || '-';
      const primaryProduct =
        getFirstValue(data, ['product_services_primary', 'primary_product', 'produk_utama']) || '-';

      const sosmedRaw = getFirstValue(data, ['social_media_marketplaces', 'social_media', 'marketplaces']);
      let sosmedStr = '-';
      if (Array.isArray(sosmedRaw) && sosmedRaw.length > 0) {
        sosmedStr = sosmedRaw
          .filter((s: any) => (s.social_media_marketplace || s.name || s.channel) && (s.account_name || s.account))
          .map((s: any) => `${s.social_media_marketplace || s.name || s.channel}: ${s.account_name || s.account}`)
          .join(', ');
      }

      const otherProdRaw = getFirstValue(data, ['other_product_services', 'other_products', 'produk_lainnya']);
      let otherProdStr = '-';
      if (Array.isArray(otherProdRaw) && otherProdRaw.length > 0) {
        otherProdStr = otherProdRaw
          .filter((p: any) => p.product_service_name || p.name)
          .map((p: any) => {
            const harga = formatPriceRange(p.estimated_price_min, p.estimated_price_max, p.estimated_price);
            return `${p.product_service_name || p.name} (${harga})`;
          })
          .join(' | ');
      }

      const picNames = Array.isArray(data.pic_names) ? data.pic_names.join(', ') : data.pic_names || '-';

      // PERANCANGAN AOS
      const purpose = getFirstValue(data, ['purpose', 'tujuan']);
      const positioning = getFirstValue(data, ['positioning', 'posisi_pasar']);
      const vision = getFirstValue(data, ['vision', 'visi']);
      const mission = getFirstValue(data, ['mission', 'misi']);
      const culture = getFirstValue(data, ['culture', 'budaya']);
      const businessGoal = getFirstValue(data, ['business_goal', 'tujuan_bisnis']);

      const rawRowObj: Record<string, any> = {
        'ID Workspace': tenant.id,
        'Nama Workspace': tenant.name || '',
        'Slug Workspace': tenant.slug || '',
        'Status Data': 'Terisi',

        // STEP 1: IDENTITAS PEMILIK
        'Nama Lengkap Owner': clientName,
        'Nama Panggilan Owner': clientNickname,
        'No. WhatsApp Owner': ownerPhone,
        'Provinsi Domisili Owner': provinceName,
        'Kota/Kabupaten Domisili Owner': regencyName,
        'Kecamatan Domisili Owner': districtName,
        'Kelurahan Domisili Owner': villageName,
        'Kode Pos Owner': postalCode,
        'Alamat Lengkap Owner': ownerAddress,
        'Jenis Kelamin Owner': ownerGender,
        'Agama Owner': ownerReligion,
        'Status Pernikahan': maritalStatus,
        'Kegiatan Pasangan': spouseActivity,
        'Deskripsi Diri Owner': selfDescription,
        'Profil DISC Owner': discProfile,
        'Komunitas / Organisasi Bisnis': bizCommunitiesStr,

        // STEP 2: IDENTITAS & OPERASIONAL BISNIS
        'Nama Bisnis / Brand': businessName,
        'Nama Legalitas Usaha': businessLegalName,
        'Badan Hukum Usaha': legalEntity,
        'Industri Usaha': industry,
        'Kategori Usaha': businessCategory,
        'Usia Usaha': businessAgeStr,
        'Jumlah Cabang Usaha': branchCountStr,
        'Status Kepemilikan Usaha': ownershipStr,
        'Status Usaha (Pusat/Cabang)': statusUsaha,
        'Jumlah Karyawan': totalEmployeesStr,
        'Omset / Skala Usaha': businessScale,
        'Provinsi Lokasi Usaha': bizProvince || '-',
        'Kota/Kabupaten Lokasi Usaha': bizRegency || '-',
        'Alamat Kantor Pusat': headOfficeAddress,
        'No. Telepon Kantor': officePhone,
        'Website Bisnis': businessWebsite,
        'Jumlah Tim Ring 1': ring1Count,
        'Latar Belakang Tim Ring 1': ring1Bg,
        'Laporan Keuangan PSAK': hasFinancialReport,
        'Sistem ERP / POS': hasErpPos,
        'Status Riba / Hutang Usaha': hasDebtRiba,

        // STEP 3: PRODUK, SOSMED & 3 MASALAH UTAMA
        'Masalah Utama 1': p1 || '-',
        'Masalah Utama 2': p2 || '-',
        'Masalah Utama 3': p3 || '-',
        'Posisi / Kondisi Bisnis': bizCondPos,
        'Komunikasi Non-Bisnis': nonBizCommPos,
        'Produk Utama': primaryProduct,
        'Produk Lainnya & Harga': otherProdStr,
        'Sosial Media & Marketplace': sosmedStr,
        'Nama PIC Pendamping': picNames,

        // AOS 12 LANGKAH
        'Purpose (Tujuan)': purpose,
        'Positioning (Posisi Pasar)': positioning,
        Visi: vision,
        Misi: mission,
        'Budaya (Culture)': culture,
        'Business Goal': businessGoal,

        // METADATA
        'Tanggal Dibuat': data.created_at || '-',
        'Terakhir Diperbarui': data.updated_at || '-',
      };

      // EKSTRAKSI DINAMIS FIELD LAINNYA
      Object.keys(data).forEach(k => {
        if (!standardKeys.has(k) && !k.startsWith('is_published_')) {
          const val = getRawValue(data, k);
          if (val !== undefined && val !== null && val !== '' && val !== '-') {
            const fieldLabel = k.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
            rawRowObj[fieldLabel] = val;
          }
        }
      });

      // Sanitizer wajib: Memastikan seluruh sel adalah string aman
      const safeRow: Record<string, string> = {};
      Object.keys(rawRowObj).forEach(key => {
        safeRow[key] = formatValueForCell(rawRowObj[key]);
      });

      compiledRows.push(safeRow);
    }

    setExportData(compiledRows);
    setLoading(false);
    setStatusMessage(`Selesai memuat 100% field lengkap dari ${workspaceList.length} workspace.`);
  };

  useEffect(() => {
    loadAllWorkspacesData();
  }, []);

  const downloadExcel = () => {
    if (exportData.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length + 4, 18),
    }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Seluruh Field Data Perusahaan');

    const fileName = `Export_Lengkap_Data_Perusahaan_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  if (!isAdminOrOwner) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center bg-white rounded-2xl shadow-sm border border-gray-100 my-12">
        <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-gray-800">Akses Dibatasi</h2>
        <p className="text-sm text-gray-500 mt-1">Halaman ini hanya dapat diakses oleh Admin / Owner.</p>
      </div>
    );
  }

  const filledCount = exportData.filter(r => r['Status Data'] === 'Terisi').length;
  const allColumns = exportData.length > 0 ? Object.keys(exportData[0]) : [];
  const displayRows = limit > 0 ? exportData.slice(0, limit) : exportData;

  return (
    <section className="w-full p-6 space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-900 rounded-2xl p-6 text-white shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl font-bold">Ekspor Seluruh Field Form Data Perusahaan</h1>
          </div>
          <p className="text-blue-100 text-sm">
            Menarik 100% lengkap seluruh field dari Step 1 (Identitas Pemilik), Step 2 (Operasional Usaha), Step 3
            (Produk, Sosmed & 3 Masalah Utama), hingga 12 Langkah AOS.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadAllWorkspacesData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/15 hover:bg-white/25 rounded-xl font-medium text-sm transition backdrop-blur-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>

          <button
            onClick={downloadExcel}
            disabled={loading || exportData.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold rounded-xl text-sm transition shadow-md disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Download Excel (.xlsx)
          </button>
        </div>
      </div>

      {/* Progress & Summary Bar */}
      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-semibold text-gray-700">
              Terisi: <strong className="text-emerald-600">{filledCount}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-semibold text-gray-700">
              Kosong: <strong className="text-gray-500">{exportData.length - filledCount}</strong>
            </span>
          </div>
          <div className="text-sm text-gray-500 pl-4 border-l border-gray-200">
            Total Workspace: <strong>{progress.total || exportData.length}</strong> | Total Kolom Field:{' '}
            <strong>{allColumns.length}</strong>
          </div>
        </div>

        <div className="text-xs text-gray-500 italic">{statusMessage}</div>
      </div>

      {/* Loading Progress Bar */}
      {loading && progress.total > 0 && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
          <div
            className="bg-blue-600 h-2.5 transition-all duration-300 rounded-full"
            style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
          />
        </div>
      )}

      {/* Dynamic Table Preview */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <h2 className="text-sm font-bold text-gray-700">
            Preview Data Workspace ({displayRows.length} dari {exportData.length} Workspace)
          </h2>

          <div className="flex items-center gap-3">
            <label className="text-xs text-gray-600 font-medium">Tampilkan Baris:</label>
            <select
              value={limit}
              onChange={e => setLimit(Number(e.target.value))}
              className="text-xs font-semibold px-3 py-1.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
            >
              <option value={0}>Semua Baris ({exportData.length})</option>
              <option value={50}>50 Baris</option>
              <option value={100}>100 Baris</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-left text-xs whitespace-nowrap">
            <thead className="bg-gray-100 text-gray-700 sticky top-0 font-semibold border-b border-gray-200 z-10">
              <tr>
                {allColumns.map(col => (
                  <th key={col} className="px-4 py-3 bg-gray-100 border-r border-gray-200">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.map((row, idx) => (
                <tr key={idx} className="hover:bg-blue-50/50 transition">
                  {allColumns.map(col => {
                    if (col === 'Status Data') {
                      const isTerisi = row[col] === 'Terisi';
                      return (
                        <td key={col} className="px-4 py-2.5 border-r border-gray-100 max-w-[280px] truncate">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] ${
                              isTerisi
                                ? 'font-semibold bg-emerald-100 text-emerald-700'
                                : 'font-medium bg-gray-100 text-gray-500'
                            }`}
                          >
                            {row[col]}
                          </span>
                        </td>
                      );
                    }
                    return (
                      <td key={col} className="px-4 py-2.5 border-r border-gray-100 max-w-[280px] truncate">
                        {row[col]}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {exportData.length === 0 && !loading && (
                <tr>
                  <td colSpan={allColumns.length || 10} className="px-4 py-8 text-center text-gray-400 italic">
                    Belum ada data workspace yang dimuat. Klik "Refresh Data" di atas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
